import { TextField, Select } from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { ChildOverviewProps } from "./types";
import { useParentCodebooks } from "../../hooks/useParentCodebooks";
import { FetchedDiffValue } from "../FetchedDiffValue";

interface IProps extends ChildOverviewProps {
  userOib: string;
}

export const ChildPersonalDataSection = ({
  childDetails,
  fetchedData,
  onUpdate,
  userOib,
}: IProps) => {
  const { t } = useTranslation();

  const { getCodebookName, countries, citizenships, nationalities, genders } =
    useParentCodebooks();

  return (
    <Row className="mb-2">
      <Col md={3}>
        <TextField
          label={t("person.firstName")}
          value={childDetails.firstName}
          readonly
          plaintext
        />
        <FetchedDiffValue
          show={
            !!fetchedData && fetchedData.firstName !== childDetails.firstName
          }
        >
          {fetchedData?.firstName}
        </FetchedDiffValue>
      </Col>
      <Col md={3}>
        <TextField
          label={t("person.lastName")}
          value={childDetails.lastName}
          readonly
          plaintext
        />
        <FetchedDiffValue
          show={!!fetchedData && fetchedData.lastName !== childDetails.lastName}
        >
          {fetchedData?.lastName}
        </FetchedDiffValue>
      </Col>
      <Col md={3}>
        <TextField label={t("person.oib")} value={userOib} readonly plaintext />
        <FetchedDiffValue
          show={
            !!fetchedData &&
            fetchedData.person?.oib !== childDetails.person?.oib
          }
        >
          {fetchedData?.person?.oib}
        </FetchedDiffValue>
      </Col>
      <Col md={3}>
        <TextField
          label={t("person.dateOfBirth")}
          value={
            childDetails.dateOfBirth
              ? new Date(childDetails.dateOfBirth).toLocaleDateString()
              : ""
          }
          readonly
          plaintext
        />
        <FetchedDiffValue
          show={
            !!fetchedData &&
            fetchedData.dateOfBirth !== childDetails.dateOfBirth
          }
        >
          {fetchedData?.dateOfBirth
            ? new Date(fetchedData.dateOfBirth).toLocaleDateString()
            : ""}
        </FetchedDiffValue>
      </Col>
      <Col md={3}>
        <TextField
          label={t("person.sex")}
          value={getCodebookName(genders, childDetails.genderId)}
          readonly
          plaintext
        />
        <FetchedDiffValue
          show={!!fetchedData && fetchedData.genderId !== childDetails.genderId}
        >
          {getCodebookName(genders, fetchedData?.genderId)}
        </FetchedDiffValue>
      </Col>
      <Col md={3}>
        <TextField
          label={t("person.birthplace")}
          value={childDetails.cityOfBirth ?? ""}
          readonly
          plaintext
        />
        <FetchedDiffValue
          show={
            !!fetchedData &&
            fetchedData.cityOfBirth !== childDetails.cityOfBirth
          }
        >
          {fetchedData?.cityOfBirth}
        </FetchedDiffValue>
      </Col>
      <Col md={3}>
        <TextField
          label={t("person.citizenship")}
          value={getCodebookName(citizenships, childDetails.citizenshipId)}
          readonly
          plaintext
        />
        <FetchedDiffValue
          show={
            !!fetchedData &&
            fetchedData.citizenshipId !== childDetails.citizenshipId
          }
        >
          {getCodebookName(citizenships, fetchedData?.citizenshipId)}
        </FetchedDiffValue>
      </Col>
      <Col md={3}>
        <TextField
          label={t("person.countryBirth")}
          value={getCodebookName(countries, childDetails.countryOfBirthId)}
          readonly
          plaintext
        />
        <FetchedDiffValue
          show={
            !!fetchedData &&
            fetchedData.countryOfBirthId !== childDetails.countryOfBirthId
          }
        >
          {getCodebookName(countries, fetchedData?.countryOfBirthId)}
        </FetchedDiffValue>
      </Col>
      <Col md={3}>
        <Select
          label={t("person.nationality")}
          value={childDetails.nationalityId ?? null}
          selectData={{
            keyProp: "id",
            valueProp: "id",
            textProp: "name",
            data: nationalities ?? [],
          }}
          onChange={(e: React.ChangeEvent<any>) =>
            onUpdate({ ...childDetails, nationalityId: Number(e.target.value) })
          }
          required
        />
        <FetchedDiffValue
          show={
            !!fetchedData &&
            fetchedData.nationalityId !== childDetails.nationalityId
          }
        >
          {getCodebookName(nationalities, fetchedData?.nationalityId)}
        </FetchedDiffValue>
      </Col>
    </Row>
  );
};
